import { useEffect } from "react";
import { useFormik } from "formik";
import {
  useAdminLeadControllerGetOne,
  useAdminLeadControllerUpdate,
} from "service";
import {
  AdminLeadResDto,
  AdminPostLeadReqDto,
} from "service/api/estabrookSchemas";
import { queryKeyFn } from "service/api/estabrookContext";
import { toFormikValidationSchema } from "zod-formik-adapter";
import { queryClient } from "@/query-client";
import { leadFormSchema } from "../utils/lead-form-schema";

// The generated AdminLeadResDto types most CRM fields as Record<string, any>,
// but the API returns primitives (and contractPrice as a string), so coerce.
const asString = (value: unknown): string | undefined =>
  value == null ? undefined : String(value);
const asNumber = (value: unknown): number | undefined => {
  if (value == null || value === "") return undefined;
  const num = Number(value);
  return Number.isNaN(num) ? undefined : num;
};

const emptyValues: AdminPostLeadReqDto = {
  firstName: "",
  lastName: "",
  email: "",
  phoneNumber: "+",
  address: "",
};

const toFormValues = (lead: AdminLeadResDto): AdminPostLeadReqDto => ({
  firstName: lead.firstName,
  lastName: lead.lastName,
  email: lead.email,
  phoneNumber: lead.phoneNumber,
  address: lead.address,
  onsiteContactName: asString(lead.onsiteContactName),
  onsiteContactEmail: asString(lead.onsiteContactEmail),
  contractPrice: asNumber(lead.contractPrice),
  billingAddress: asString(lead.billingAddress),
  financialNotes: asString(lead.financialNotes),
  squareFootage: asNumber(lead.squareFootage),
  // The API returns null for unset enums, but z.union(...).optional() only
  // accepts undefined — coerce falsy (null / "") values to undefined.
  slope: lead.slope || undefined,
  condition: lead.condition || undefined,
  difficulty: asNumber(lead.difficulty),
  notes: asString(lead.notes),
});

const useEditLeadForm = (leadId: string, onFormSuccess: () => void) => {
  const { data, isLoading } = useAdminLeadControllerGetOne({
    pathParams: { leadId },
  });
  const lead = data?.data;

  const { mutate, isLoading: isSaving } = useAdminLeadControllerUpdate({
    onSuccess: () => {
      // Invalidate both the list and this lead's detail query, otherwise
      // reopening the edit page would seed from stale cached detail data.
      queryClient.invalidateQueries(
        queryKeyFn({
          path: "/api/v1/admin/leads",
          operationId: "adminLeadControllerGetAll",
          variables: {},
        }),
      );
      queryClient.invalidateQueries(
        queryKeyFn({
          path: "/api/v1/admin/leads/{leadId}",
          operationId: "adminLeadControllerGetOne",
          variables: { pathParams: { leadId } },
        }),
      );
      onFormSuccess();
    },
  });

  const leadForm = useFormik<AdminPostLeadReqDto>({
    initialValues: emptyValues,
    validationSchema: toFormikValidationSchema(leadFormSchema),
    onSubmit: (values) => {
      mutate({ pathParams: { leadId }, body: { ...values } });
    },
  });

  // Seed the form from the fetched lead whenever the data changes, but never
  // while the form is dirty — that guards against wiping the user's in-progress
  // edits, while still re-seeding with fresh data on reopen (after invalidation
  // the detail query refetches and this runs again).
  useEffect(() => {
    if (lead && !leadForm.dirty) {
      leadForm.resetForm({ values: toFormValues(lead) });
    }
  }, [lead]);

  return { leadForm, lead, isLoading, isSaving };
};

export default useEditLeadForm;
